import { create } from 'zustand';
import { useAuthStore } from './auth';
import {
  initializeMessaging,
  getFCMToken,
  setupForegroundMessageHandler,
  isFCMConfigured,
  getFirebaseConfig,
} from '@/lib/firebase';
import {
  registerTokenWithBackend,
  unregisterTokenFromBackend,
  requestNotificationPermission,
} from '@/lib/fcm-utils';

// LocalStorage keys
const STORAGE_KEYS = {
  UNREAD_COUNT: 'notifications_unread_count',
  PENDING_COUNT: 'notifications_pending_count',
  LAST_FETCHED: 'notifications_last_fetched',
} as const;

// Cache expiration time: 30 minutes
const CACHE_EXPIRY_MS = 30 * 60 * 1000;

interface CachedCount {
  count: number;
  lastFetched: number;
}

/**
 * LocalStorage helper functions for caching counts with stale-while-revalidate pattern
 */
const getCachedCount = (key: string): CachedCount | null => {
  if (typeof window === 'undefined') return null;
  try {
    const cached = localStorage.getItem(key);
    if (!cached) return null;
    return JSON.parse(cached) as CachedCount;
  } catch {
    return null;
  }
};

const setCachedCount = (key: string, count: number): void => {
  if (typeof window === 'undefined') return;
  try {
    const cached: CachedCount = {
      count,
      lastFetched: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cached));
    
    // Storage events are fired automatically in other tabs.
  } catch (error) {
    console.error(`[Notifications] Failed to cache ${key}:`, error);
  }
};

const isCacheStale = (cached: CachedCount | null): boolean => {
  if (!cached) return true;
  const age = Date.now() - cached.lastFetched;
  return age >= CACHE_EXPIRY_MS;
};

const loadCachedCount = (key: string): { count: number; isStale: boolean } => {
  const cached = getCachedCount(key);
  if (cached) {
    return {
      count: cached.count,
      isStale: isCacheStale(cached),
    };
  }
  return { count: 0, isStale: true };
};

const clearAllCachedCounts = (): void => {
  if (typeof window === 'undefined') return;
  try {
    // Explicitly remove each key to be absolutely sure
    localStorage.removeItem(STORAGE_KEYS.UNREAD_COUNT);
    localStorage.removeItem(STORAGE_KEYS.PENDING_COUNT);
    localStorage.removeItem(STORAGE_KEYS.LAST_FETCHED);
    
    // Also remove any other notification-related keys that might exist
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('notifications_') || key.startsWith('fcm_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    
    // Storage events are fired automatically in other tabs on removeItem.
  } catch (error) {
    console.error('[Notifications] Failed to clear cache:', error);
  }
};

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
  link?: string | null;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  pendingCount: number;
  loading: boolean;
  fcmToken: string | null;
  fcmInitialized: boolean;
  permissionGranted: boolean | null;
  // Actions
  fetchNotifications: (force?: boolean) => Promise<void>;
  fetchPendingCount: (force?: boolean) => Promise<void>;
  setPendingCount: (count: number) => void;
  setUnreadCount: (count: number) => void;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => void;
  addNotification: (notification: Notification) => void;
  initializeFCM: () => Promise<void>;
  requestNotificationPermission: () => Promise<boolean>;
  registerFCMToken: (token: string) => Promise<void>;
  unregisterFCMToken: () => Promise<void>;
  /** Reset FCM state so it can be re-initialized (e.g. when user re-enables notifications). */
  resetFCMState: () => void;
  clearStore: () => void;
}


export const useNotificationsStore = create<NotificationsState>((set, get) => {
  // Initialize from LocalStorage cache if available (stale-while-revalidate)
  let initialUnreadCount = 0;
  let initialPendingCount = 0;
  let shouldFetchUnread = false;
  let shouldFetchPending = false;

  if (typeof window !== 'undefined') {
    const unreadCache = loadCachedCount(STORAGE_KEYS.UNREAD_COUNT);
    const pendingCache = loadCachedCount(STORAGE_KEYS.PENDING_COUNT);
    
    initialUnreadCount = unreadCache.count;
    initialPendingCount = pendingCache.count;
    
    // If cache is stale, trigger background fetch
    shouldFetchUnread = unreadCache.isStale;
    shouldFetchPending = pendingCache.isStale;
  }

  // Setup multi-tab sync via storage events (will be set up in a useEffect in the component)
  // Note: Storage events only fire in other tabs, not the current one

  // Trigger background fetches if cache is stale (stale-while-revalidate)
  if (shouldFetchUnread) {
    // Use setTimeout to ensure store is initialized first
    setTimeout(() => {
      get().fetchNotifications(false);
    }, 0);
  }
  
  if (shouldFetchPending) {
    setTimeout(() => {
      get().fetchPendingCount(false);
    }, 0);
  }

  return {
    notifications: [],
    unreadCount: initialUnreadCount,
    pendingCount: initialPendingCount,
    loading: false,
    fcmToken: null,
    fcmInitialized: false,
    permissionGranted: null,

  fetchNotifications: async (force = false) => {
    const authStore = useAuthStore.getState();
    // BFF: Check only isLoggedIn - session is managed by backend via HttpOnly cookies
    if (!authStore.isLoggedIn) {
      set({ notifications: [], unreadCount: 0 });
      setCachedCount(STORAGE_KEYS.UNREAD_COUNT, 0);
      return;
    }

    // Stale-while-revalidate: Check cache first unless forced
    if (!force) {
      const cached = getCachedCount(STORAGE_KEYS.UNREAD_COUNT);
      if (cached && !isCacheStale(cached)) {
        // Cache is fresh, use it and return early
        set({ unreadCount: cached.count });
        return;
      }
      // Cache is stale or missing - use cached value immediately (if available) and fetch in background
      if (cached) {
        set({ unreadCount: cached.count });
      }
    }

    set({ loading: true });
    try {
      const response = await authStore.authorizedFetch(
        '/api/notifications'
      );

      if (!response.ok) {
        if (response.status === 404) {
          set({ notifications: [], unreadCount: 0, loading: false });
          return;
        }
        console.error('[Notifications] Failed to fetch notifications', response.status);
        set({ loading: false });
        return;
      }

      const data = await response.json();
      const apiNotifications = Array.isArray(data.notifications) ? data.notifications : [];
      
      // Map API format to our format
      const notifications: Notification[] = apiNotifications.map((n: {
        id: string;
        type: string;
        title: string;
        message: string;
        is_read: boolean;
        link: string | null;
        created_at: string;
      }) => ({
        id: n.id,
        type: (n.type as Notification['type']) || 'info',
        title: n.title,
        message: n.message,
        read: n.is_read,
        created_at: n.created_at,
        link: n.link,
      }));

      // Use unread_count from API if available, otherwise calculate from list (fallback)
      const unreadCount = typeof data.unread_count === 'number' 
        ? data.unread_count 
        : notifications.filter((n) => !n.read).length;

      // Persist to LocalStorage
      setCachedCount(STORAGE_KEYS.UNREAD_COUNT, unreadCount);

      set({
        notifications,
        unreadCount,
        loading: false,
      });
    } catch (error) {
      console.error('[Notifications] Error fetching notifications', error);
      set({ loading: false });
    }
  },

  fetchPendingCount: async (force = false) => {
    const authStore = useAuthStore.getState();
    // Only fetch if user is logged in and is admin
    if (!authStore.isLoggedIn || !authStore.user?.is_master_admin) {
      set({ pendingCount: 0 });
      setCachedCount(STORAGE_KEYS.PENDING_COUNT, 0);
      return;
    }

    // Stale-while-revalidate: Check cache first unless forced
    if (!force) {
      const cached = getCachedCount(STORAGE_KEYS.PENDING_COUNT);
      if (cached && !isCacheStale(cached)) {
        // Cache is fresh, use it and return early
        set({ pendingCount: cached.count });
        return;
      }
      // Cache is stale or missing - use cached value immediately (if available) and fetch in background
      if (cached) {
        set({ pendingCount: cached.count });
      }
    }

    try {
      // Use server action instead of direct API call
      const { getPendingCruisesCount } = await import('@/actions/admin');
      const result = await getPendingCruisesCount();
      
      if (result.error) {
        console.error('[Notifications] Failed to fetch pending approvals count:', result.error);
        set({ pendingCount: 0 });
        setCachedCount(STORAGE_KEYS.PENDING_COUNT, 0);
      } else {
        const count = result.count;
        // Persist to LocalStorage
        setCachedCount(STORAGE_KEYS.PENDING_COUNT, count);
        set({ pendingCount: count });
      }
    } catch (err) {
      console.error('[Notifications] Failed to fetch pending approvals count:', err);
      set({ pendingCount: 0 });
      setCachedCount(STORAGE_KEYS.PENDING_COUNT, 0);
    }
  },

  addNotification: (notification: Notification) => {
    const state = get();
    // Check if notification already exists (avoid duplicates)
    const exists = state.notifications.some((n) => n.id === notification.id);
    if (exists) {
      return;
    }

    const newUnreadCount = notification.read ? state.unreadCount : state.unreadCount + 1;
    
    // Persist to LocalStorage
    setCachedCount(STORAGE_KEYS.UNREAD_COUNT, newUnreadCount);
    
    set({
      notifications: [notification, ...state.notifications],
      unreadCount: newUnreadCount,
    });
  },

  markAsRead: async (notificationId: string) => {
    const authStore = useAuthStore.getState();
    // BFF: Check only isLoggedIn - session is managed by backend via HttpOnly cookies
    if (!authStore.isLoggedIn) {
      return;
    }

    // Update local state optimistically
    const state = get();
    const notification = state.notifications.find((n) => n.id === notificationId);
    if (notification && !notification.read) {
      notification.read = true;
      const newUnreadCount = Math.max(0, state.unreadCount - 1);
      
      // Persist to LocalStorage
      setCachedCount(STORAGE_KEYS.UNREAD_COUNT, newUnreadCount);
      
      set({
        notifications: [...state.notifications],
        unreadCount: newUnreadCount,
      });
    }

    // Update on backend
    try {
      const response = await authStore.authorizedFetch(
        `/api/notifications/${notificationId}`,
        { method: 'POST' }
      );

      if (!response.ok) {
        console.error('[Notifications] Failed to mark notification as read', response.status);
        // Revert optimistic update on error
        if (notification) {
          notification.read = false;
          set({
            notifications: [...state.notifications],
            unreadCount: state.unreadCount + 1,
          });
        }
      }
    } catch (error) {
      console.error('[Notifications] Error marking notification as read', error);
      // Revert optimistic update on error
      if (notification) {
        notification.read = false;
        set({
          notifications: [...state.notifications],
          unreadCount: state.unreadCount + 1,
        });
      }
    }
  },

  markAllAsRead: async () => {
    const authStore = useAuthStore.getState();
    // BFF: Check only isLoggedIn - session is managed by backend via HttpOnly cookies
    if (!authStore.isLoggedIn) {
      return;
    }

    // Update local state optimistically
    const state = get();
    state.notifications.forEach((n) => {
      n.read = true;
    });
    // Persist to LocalStorage
    setCachedCount(STORAGE_KEYS.UNREAD_COUNT, 0);
    
    set({
      notifications: [...state.notifications],
      unreadCount: 0,
    });

    // Update on backend
    try {
      const response = await authStore.authorizedFetch(
        '/api/notifications',
        { method: 'POST' }
      );

      if (!response.ok) {
        console.error('[Notifications] Failed to mark all notifications as read', response.status);
        // Revert optimistic update on error
        const unreadCount = state.notifications.filter((n) => !n.read).length;
        state.notifications.forEach((n) => {
          if (!n.read) {
            n.read = false;
          }
        });
        set({
          notifications: [...state.notifications],
          unreadCount,
        });
      }
    } catch (error) {
      console.error('[Notifications] Error marking all notifications as read', error);
      // Revert optimistic update on error
      const unreadCount = state.notifications.filter((n) => !n.read).length;
      state.notifications.forEach((n) => {
        if (!n.read) {
          n.read = false;
        }
      });
      set({
        notifications: [...state.notifications],
        unreadCount,
      });
    }
  },

  setPendingCount: (count: number) => {
    setCachedCount(STORAGE_KEYS.PENDING_COUNT, count);
    set({ pendingCount: count });
  },

  setUnreadCount: (count: number) => {
    setCachedCount(STORAGE_KEYS.UNREAD_COUNT, count);
    set({ unreadCount: count });
  },

  deleteNotification: (notificationId: string) => {
    const state = get();
    const notification = state.notifications.find((n) => n.id === notificationId);
    const newNotifications = state.notifications.filter((n) => n.id !== notificationId);
    const unreadCount = notification && !notification.read
      ? Math.max(0, state.unreadCount - 1)
      : state.unreadCount;

    // Persist to LocalStorage
    setCachedCount(STORAGE_KEYS.UNREAD_COUNT, unreadCount);

    set({ notifications: newNotifications, unreadCount });
  },

  requestNotificationPermission: async () => {
    const granted = await requestNotificationPermission();
    set({ permissionGranted: granted });
    return granted;
  },

  initializeFCM: async () => {
    if (typeof window === 'undefined') {
      return;
    }

    const state = get();
    // If already initialized AND permission granted, nothing to do.
    // If initialized but permission NOT granted (e.g. dismissed), we allow retrying.
    if (state.fcmInitialized && state.permissionGranted) {
      return;
    }

    try {
      // Request notification permission first so the browser prompt always appears
      // (e.g. for admins). Do this before checking FCM config so permission is
      // requested even when Firebase env vars are missing (e.g. in dev).
      const permissionGranted = await get().requestNotificationPermission();
      if (!permissionGranted) {
        console.warn('[FCM] Notification permission not granted');
        set({ fcmInitialized: true, permissionGranted: false });
        return;
      }

      // After permission granted, check if FCM is configured (needed for token)
      if (!isFCMConfigured()) {
        set({ fcmInitialized: true, permissionGranted: true });
        return;
      }

      // Initialize messaging
      const msg = initializeMessaging();
      if (!msg) {
        // Silently fail if Firebase is not configured or not supported
        // This is expected in development or if FCM is not set up
        set({ fcmInitialized: true, permissionGranted: false });
        return;
      }

      // Get FCM token
      const token = await getFCMToken(msg);

      if (token) {
        set({ fcmToken: token, fcmInitialized: true, permissionGranted: true });

        // Send Firebase config to service worker so it can handle background messages (e.g. when config wasn't available on first load)
        if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
          const firebaseConfig = getFirebaseConfig();
          if (firebaseConfig.apiKey && firebaseConfig.projectId) {
            navigator.serviceWorker.controller.postMessage({
              type: 'FIREBASE_CONFIG',
              config: firebaseConfig,
            });
          }
        }

        // Register token with backend
        const authStore = useAuthStore.getState();
        if (authStore.isLoggedIn) {
          await registerTokenWithBackend(token, authStore.authorizedFetch);
          // Ensure notifications are enabled on the user profile
          if (authStore.user?.notifications_enabled === false) {
             await authStore.patchUser({ notifications_enabled: true });
          }
        }

        // Setup foreground message handler
        setupForegroundMessageHandler(msg, (payload: {
          messageId?: string;
          notification?: { title?: string; body?: string; icon?: string };
          data?: { 
            notification_id?: string; 
            type?: string; 
            title?: string; 
            message?: string; 
            link?: string;
            count?: string; // For PENDING_UPDATE and NOTIFICATION_UPDATE
          };
        }) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('[FCM] Foreground message received:', payload);
          }

          const messageType = payload.data?.type;

          // Handle PENDING_UPDATE messages for admin pending cruises
          if (messageType === 'PENDING_UPDATE' || messageType === 'PENDING_UPDATED') {
            if (process.env.NODE_ENV === 'development') {
              console.log('[FCM] PENDING_UPDATE message received');
            }
            
            // Update count from payload if provided, otherwise fetch
            if (payload.data?.count !== undefined) {
              const count = parseInt(payload.data.count, 10) || 0;
              setCachedCount(STORAGE_KEYS.PENDING_COUNT, count);
              set({ pendingCount: count });
            } else {
              // Fetch fresh count when notification arrives
              get().fetchPendingCount(true);
            }
            
            // Still add the notification if it has notification data
            if (payload.notification || payload.data?.title) {
              const notification: Notification = {
                id: payload.data?.notification_id || payload.messageId || `fcm-${Date.now()}`,
                type: 'info',
                title: payload.notification?.title || payload.data?.title || 'Pending Cruises Updated',
                message: payload.notification?.body || payload.data?.message || '',
                read: false,
                created_at: new Date().toISOString(),
                link: payload.data?.link || null,
              };
              get().addNotification(notification);
            }
            return;
          }

          // Handle NOTIFICATION_UPDATE messages
          if (messageType === 'NOTIFICATION_UPDATE') {
            if (process.env.NODE_ENV === 'development') {
              console.log('[FCM] NOTIFICATION_UPDATE message received');
            }
            
            // Update count from payload if provided, otherwise fetch
            if (payload.data?.count !== undefined) {
              const count = parseInt(payload.data.count, 10) || 0;
              setCachedCount(STORAGE_KEYS.UNREAD_COUNT, count);
              set({ unreadCount: count });
            } else {
              // Fetch fresh count when notification arrives
              get().fetchNotifications(true);
            }
            
            // Still add the notification if it has notification data
            if (payload.notification || payload.data?.title) {
              const notification: Notification = {
                id: payload.data?.notification_id || payload.messageId || `fcm-${Date.now()}`,
                type: 'info',
                title: payload.notification?.title || payload.data?.title || 'New Notification',
                message: payload.notification?.body || payload.data?.message || '',
                read: false,
                created_at: new Date().toISOString(),
                link: payload.data?.link || null,
              };
              get().addNotification(notification);
            }
            return;
          }

          // Handle regular notifications
          const notification: Notification = {
            id: payload.data?.notification_id || payload.messageId || `fcm-${Date.now()}`,
            type: (payload.data?.type as Notification['type']) || 'info',
            title: payload.notification?.title || payload.data?.title || 'New Notification',
            message: payload.notification?.body || payload.data?.message || '',
            read: false,
            created_at: new Date().toISOString(),
            link: payload.data?.link || null,
          };

          // Add notification to store (this will update unreadCount and cache)
          get().addNotification(notification);
        });
      } else {
        console.warn('[FCM] No FCM token available');
        set({ fcmInitialized: true, permissionGranted: true });
      }
    } catch (error) {
      console.error('[FCM] Error initializing FCM:', error);
      set({ fcmInitialized: true });
    }
  },

  registerFCMToken: async (token: string) => {
    const authStore = useAuthStore.getState();
    // BFF: Check only isLoggedIn - session is managed by backend via HttpOnly cookies
    if (!authStore.isLoggedIn) {
      return;
    }

    await registerTokenWithBackend(token, authStore.authorizedFetch);
  },

  unregisterFCMToken: async () => {
    const authStore = useAuthStore.getState();
    const state = get();

    if (!authStore.isLoggedIn || !state.fcmToken) {
      set({ fcmToken: null, fcmInitialized: false });
      return;
    }

    const success = await unregisterTokenFromBackend(state.fcmToken, authStore.authorizedFetch);
    if (success) {
      set({ fcmToken: null, fcmInitialized: false });
    }
  },

  resetFCMState: () => {
    set({ fcmInitialized: false, fcmToken: null });
  },

  clearStore: () => {
    clearAllCachedCounts();
    set({
      notifications: [],
      unreadCount: 0,
      pendingCount: 0,
      fcmToken: null,
      fcmInitialized: false,
      permissionGranted: null,
    });
  },
  };
});
